import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  signal,
  ViewChild,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent, debounceTime } from 'rxjs';
import { WalletComponent } from '@app/shared/components/wallet/wallet.component';
import { ProgressBarComponent } from '@app/shared/components/progress-bar/progress-bar.component';
import { LoadingComponent } from '@app/shared/components/loading/loading.component';
import { SharedModule } from '@app/shared/shared.module';
import { NativeService } from '@app/shared/services/native.service';
import { EventManager } from '@app/shared/utils/event-manager';
import { Modal, ModalType, NonPersistedModalName } from '@app/shared/models/modal.model';
import { RecurringComponent } from '../recurring/recurring.component';
import { ProgressLineFacade } from '../progress-line.facade';
import { MilestoneComponent } from './milestone/milestone.component';
import { ProgressIndicatorComponent } from './progress-indicator/progress-indicator.component';
import { EndGameRewardComponent } from './end-game-reward/end-game-reward.component';
import {
  calcCurrentSegmentPoints,
  calcMilestoneCentersPx,
  calcMilestonePositions,
  calcNextSegmentRequired,
  calcSegmentProgress,
  clampScrollLeft,
  findCurrentSegmentIndex,
} from '../progress-line.math';
import { GlobalDataRepository } from '@app/shared/state/global-data/global-data.repository';
import { DragScrollDirective, DragScrollHost } from '@app/shared/directives/drag-and-scroll.directive';
import { JulietBuddyComponent } from '@app/shared/components/juliet-buddy/juliet-buddy.component';
import { UserRepository } from '@app/shared/state/user/user.repository';
import { RecurringFacade } from '../recurring/recurring.facade';
import { RECURRING_ONBOARDING_POSE } from '@app/shared/components/juliet-buddy/juliet-buddy.config';

const ARROW_MARGIN = 30;
const RESIZE_DEBOUNCE_MS = 100;

@Component({
  selector: 'slotcom-progress-line-casino',
  templateUrl: './casino.component.html',
  styleUrls: ['./casino.component.scss'],
  standalone: true,
  imports: [
    WalletComponent,
    RecurringComponent,
    ProgressBarComponent,
    LoadingComponent,
    SharedModule,
    MilestoneComponent,
    ProgressIndicatorComponent,
    EndGameRewardComponent,
    DragScrollDirective,
    JulietBuddyComponent,
  ],
})
export class ProgressLineCasinoComponent implements OnInit, AfterViewInit {
  private readonly progressLineFacade = inject(ProgressLineFacade);
  private readonly nativeService = inject(NativeService);
  private readonly eventManager = inject(EventManager);
  private readonly destroyRef = inject(DestroyRef);
  private readonly globalDataRepository = inject(GlobalDataRepository);
  private readonly userRepository = inject(UserRepository);
  private readonly recurringFacade = inject(RecurringFacade);

  private readonly userRecurringHasEverBeenRedeemed = this.recurringFacade.hasEverBeenRedeemed;

  readonly showRecurringOnboarding = computed(
    () => !this.userRecurringHasEverBeenRedeemed() && !this.userRepository.recurringLocked()
  );

  readonly recurringOnboardingPose = computed(() => {
    this.layoutReady();
    return { ...RECURRING_ONBOARDING_POSE };
  });

  readonly casino = this.progressLineFacade.casino;
  readonly casinoTier = this.progressLineFacade.casinoTier;
  readonly prizesUI = this.progressLineFacade.prizesUI;
  readonly absoluteCurrent = this.progressLineFacade.absoluteCurrent;
  readonly casinoMilestones = this.progressLineFacade.casinoMilestones;
  readonly cumulativeThresholds = this.progressLineFacade.cumulativeThresholds;

  casinoName = computed(() => {
    return `home.progress-line.casino-${this.casino().order}.name`;
  });

  @ViewChild('scrollContainer', { read: ElementRef })
  scrollContainerRef!: ElementRef<HTMLElement>;

  @ViewChild('cssMeasure', { read: ElementRef })
  cssMeasureRef!: ElementRef<HTMLElement>;

  @ViewChild('milestonesTrack', { read: ElementRef })
  milestonesTrackRef!: ElementRef<HTMLElement>;

  @ViewChildren('milestoneBox', { read: ElementRef })
  milestoneBoxRefs!: QueryList<ElementRef<HTMLElement>>;

  private readonly milestoneElWidthPx = signal(0);
  private readonly milestonesTrackOffsetLeft = signal(0);

  readonly scrollOffset = signal(0);
  readonly scrollContainerEl = signal<HTMLElement | null>(null);
  readonly showArrows = signal(false);
  readonly showScrollNext = signal(false);
  readonly showScrollBack = signal(false);

  readonly milestonePositions = computed(() => calcMilestonePositions(this.casinoMilestones()?.length ?? 0));

  private readonly currentSegmentIndex = computed(() =>
    findCurrentSegmentIndex(this.cumulativeThresholds(), this.absoluteCurrent())
  );

  readonly currentSegmentPoints = computed(() =>
    calcCurrentSegmentPoints(this.cumulativeThresholds(), this.absoluteCurrent(), this.currentSegmentIndex())
  );

  readonly nextSegmentRequired = computed(() =>
    calcNextSegmentRequired(this.casinoMilestones(), this.currentSegmentIndex())
  );

  readonly segmentProgress = computed(() =>
    calcSegmentProgress(
      this.casinoMilestones()?.length ?? 0,
      this.currentSegmentIndex(),
      this.currentSegmentPoints(),
      this.nextSegmentRequired()
    )
  );

  readonly milestoneTrackWidthPx = computed(() => {
    const count = this.casinoMilestones()?.length ?? 0;
    const elWidth = this.milestoneElWidthPx();
    if (count < 2 || elWidth === 0) return 0;
    return (count - 1) * elWidth;
  });

  readonly milestoneCentersPx = computed(() =>
    calcMilestoneCentersPx(
      this.milestonePositions(),
      this.milestoneTrackWidthPx(),
      this.milestoneElWidthPx(),
      this.milestonesTrackOffsetLeft()
    )
  );

  readonly trackLeftPx = computed(() => {
    const centers = this.milestoneCentersPx();
    return centers.length > 0 ? centers[0] : 0;
  });

  readonly trackWidthPx = computed(() => {
    const centers = this.milestoneCentersPx();
    return centers.length < 2 ? 0 : centers[centers.length - 1] - centers[0];
  });

  readonly layoutReady = computed(() => {
    const milestones = this.casinoMilestones();
    if (!milestones?.length) return false;
    const centers = this.milestoneCentersPx();
    return centers.length === milestones.length && centers.length > 0;
  });

  private containerResizeObserver: ResizeObserver | null = null;
  private hasCenteredOnInit = false;

  private get scrollEl(): HTMLElement | null {
    return this.scrollContainerRef?.nativeElement ?? null;
  }

  async ngOnInit(): Promise<void> {
    if (!this.globalDataRepository.getCurrentGame()) {
      await this.progressLineFacade.refreshUserCasino();
    }

    this.listenToWindowResize();
  }

  ngAfterViewInit(): void {
    if (this.scrollContainerRef) {
      this.scrollContainerEl.set(this.scrollContainerRef.nativeElement);
    }
    this.refreshArrows();
    this.readResolvedDimensions();
    this.setupContainerResizeObserver();

    this.milestoneBoxRefs.changes.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.hasCenteredOnInit = false;
      this.readResolvedDimensions();
    });
  }

  onScrollContainerScroll(): void {
    this.scrollOffset.set(this.scrollContainerRef?.nativeElement?.scrollLeft ?? 0);
    this.updateArrowVisibility();
  }

  centerOnCurrent(): void {
    const vp = this.scrollEl;
    const width = this.trackWidthPx();
    if (!vp || !width) return;

    const progressContentX = this.trackLeftPx() + this.segmentProgress() * width;
    const targetLeft = clampScrollLeft(progressContentX - vp.clientWidth / 2, vp.scrollWidth, vp.clientWidth);

    const dragScroll = (vp as DragScrollHost).dragScrollRef;
    if (dragScroll) {
      dragScroll.smoothScrollTo(targetLeft);
    } else {
      vp.scrollLeft = targetLeft;
    }
  }

  closeModal(): void {
    this.eventManager.closeModal(new Modal(NonPersistedModalName.ProgressLineCasinoComponent, ModalType.View));
  }

  openInfo(): void {
    this.eventManager.openModal(new Modal(NonPersistedModalName.InfoCasinoComponent, ModalType.Info));
  }

  openMap(): void {
    this.eventManager.openModal(new Modal(NonPersistedModalName.CasinoMapComponent, ModalType.View, this.casino()));
  }

  private listenToWindowResize(): void {
    fromEvent(window, 'resize')
      .pipe(debounceTime(RESIZE_DEBOUNCE_MS), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.refreshArrows();
        this.readResolvedDimensions();
      });
  }

  private refreshArrows(): void {
    this.checkArrows();
    this.updateArrowVisibility();
  }

  private readResolvedDimensions(): void {
    const measureEl = this.cssMeasureRef?.nativeElement;
    if (!measureEl) return;

    const milestoneWidth = measureEl.offsetWidth;

    if (milestoneWidth > 0) this.milestoneElWidthPx.set(milestoneWidth);

    this.measureTrackOffset();
    this.tryCenterOnInit();
  }

  private measureTrackOffset(): void {
    const trackEl = this.milestonesTrackRef?.nativeElement;
    const scrollEl = this.scrollContainerRef?.nativeElement;
    if (!trackEl || !scrollEl) return;

    let offsetX = 0;
    let current: HTMLElement = trackEl;
    while (current && current !== scrollEl) {
      offsetX += current.offsetLeft;
      current = current.offsetParent as HTMLElement;
    }
    this.milestonesTrackOffsetLeft.set(offsetX);
  }

  private tryCenterOnInit(): void {
    if (this.hasCenteredOnInit || !this.layoutReady()) return;
    this.hasCenteredOnInit = true;
    setTimeout(() => this.centerOnCurrent(), 0);
  }

  private updateArrowVisibility(): void {
    const el = this.scrollEl;
    if (!el) return;
    this.showScrollNext.set(el.scrollLeft + el.clientWidth < el.scrollWidth - ARROW_MARGIN);
    this.showScrollBack.set(el.scrollLeft >= ARROW_MARGIN);
  }

  private checkArrows(): void {
    if (this.nativeService.isNative()) return;
    const el = this.scrollEl;
    this.showArrows.set(!!el && el.clientWidth !== el.scrollWidth);
  }

  private setupContainerResizeObserver(): void {
    this.containerResizeObserver?.disconnect();
    const scrollEl = this.scrollContainerRef?.nativeElement;
    if (!scrollEl) return;

    this.containerResizeObserver = new ResizeObserver(() => this.readResolvedDimensions());
    this.containerResizeObserver.observe(scrollEl);
    this.destroyRef.onDestroy(() => this.containerResizeObserver?.disconnect());
  }
}
